import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '../../api/client.js'
import { endpoints } from '../../api/endpoints.js'
import { listDocuments, uploadDocument } from '../../api/documents.js'
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../../api/notifications.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import { formatDateTime, statusColor, unwrapList } from '../../utils/format.js'
import { useAuth } from '../../auth/AuthProvider.jsx'

// ---------------------------------------------------------------------------
// Thin read helpers used only by this page. Kept inline so we don't
// prematurely extend api/encounters.js / api/orders.js with patient-biased
// filtering — patient scoping already happens server-side via the viewset
// get_queryset, so a plain list call is all we need.
// ---------------------------------------------------------------------------
async function listMy(endpoint) {
  const { data } = await api.get(endpoint, { params: { ordering: '-created_at', page_size: 20 } })
  return unwrapList(data)
}

const getMyEncounters   = () => listMy(endpoints.encounterList)
const getMyPrescriptions = () => listMy(endpoints.prescriptionList)
const getMyLabOrders     = () => listMy(endpoints.labOrderList)
const getMyImagingOrders = () => listMy(endpoints.imagingOrderList)
const getMyAmbulanceRequests = () => listMy(endpoints.ambulanceList)

export default function PatientHome() {
  const { user, refreshUser } = useAuth()
  const qc = useQueryClient()
  const profileId = user?.profile?.id
  const fullName = user?.full_name || user?.name || user?.username

  const encQ     = useQuery({ queryKey: ['me-enc'],     queryFn: getMyEncounters })
  const rxQ      = useQuery({ queryKey: ['me-rx'],      queryFn: getMyPrescriptions })
  const labQ     = useQuery({ queryKey: ['me-lab'],     queryFn: getMyLabOrders })
  const imgQ     = useQuery({ queryKey: ['me-img'],     queryFn: getMyImagingOrders })
  const ambQ     = useQuery({ queryKey: ['me-amb'],     queryFn: getMyAmbulanceRequests })
  const docsQ    = useQuery({ queryKey: ['me-docs'],    queryFn: () => listDocuments() })
  const notifQ   = useQuery({ queryKey: ['me-notif'],   queryFn: () => listNotifications(), refetchInterval: 10_000 })

  const markRead = useMutation({
    mutationFn: (id) => markNotificationRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-notif'] }),
  })
  const markAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me-notif'] }),
  })

  // ---- self-upload form state ----
  const [file, setFile] = useState(null)
  const [kind, setKind] = useState('id_document')
  const [description, setDescription] = useState('')
  const [uploadErr, setUploadErr] = useState(null)

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Choose a file first.')
      if (!profileId) throw new Error('Your patient profile is not ready yet.')
      return uploadDocument({
        owner_model: 'patients.PatientProfile',
        owner_id: profileId,
        file,
        kind,
        description,
      })
    },
    onSuccess: () => {
      setFile(null)
      setDescription('')
      setUploadErr(null)
      qc.invalidateQueries({ queryKey: ['me-docs'] })
    },
    onError: (e) => setUploadErr(e),
  })

  const unreadCount = (notifQ.data || []).filter((n) => !n.read_at).length

  // First-visit welcome banner: shows when the patient has no encounters
  // yet AND they haven't dismissed it. Sticky-by-user via localStorage.
  const isNewPatient = !encQ.isLoading && (encQ.data || []).length === 0
  const dismissKey = `wantok.welcome.dismissed.${user?.id || 'anon'}`
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    try { return localStorage.getItem(dismissKey) === '1' } catch { return false }
  })
  const [openItem, setOpenItem] = useState(null)  // index of the row whose inline form is expanded

  // Tab state for the records area. Notifications sits FIRST so unread
  // alerts are the first thing the patient sees on landing — same idea
  // as a phone's notification tray. Replaces the long-scroll layout
  // (Notifications + Encounters + Rx + Lab + Imaging + Ambulance +
  // Documents) with one tab strip — same prominent style as the
  // doctor / org / settings dashboards.
  const [recordsTab, setRecordsTab] = useState('notifications')
  function dismissWelcome() {
    try { localStorage.setItem(dismissKey, '1') } catch {}
    setWelcomeDismissed(true)
  }
  const showWelcome = isNewPatient && !welcomeDismissed
  const profile = user?.profile || {}

  // ---- Inline action mutations -----------------------------------------
  // PATCH the patient profile to set the emergency contact. Reuses the
  // existing /api/patients/{id}/ viewset endpoint (PATCH allowed for
  // patients on their own row). On success, re-fetch /api/auth/me/ via
  // AuthProvider's exposed refresh so the checklist auto-updates.
  const patchProfile = useMutation({
    mutationFn: async (patch) => {
      const { data } = await api.patch(
        `${endpoints.patientProfileBase}${profileId}/`, patch
      )
      return data
    },
    onSuccess: () => {
      // Refresh user state so profile.* fields update on the checklist.
      qc.invalidateQueries()
      refreshUser?.()
      setOpenItem(null)
    },
  })

  const enrollOrg = useMutation({
    mutationFn: async (code) => {
      const { data } = await api.post(endpoints.patientEnrollOrg, { code })
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries()
      refreshUser?.()
      setOpenItem(null)
    },
  })

  const checklist = [
    {
      done: Boolean(profile.date_of_birth),
      label: 'Add your date of birth',
      hint: 'Helps clinicians provide age-appropriate care.',
      kind: 'date_of_birth',
    },
    {
      done: Boolean(profile.phone_number),
      label: 'Add a phone number',
      hint: 'Receive SMS appointment reminders and lab-result alerts.',
      kind: 'phone_number',
    },
    {
      done: Boolean(profile.emergency_contact_name && profile.emergency_contact_phone),
      label: 'Add an emergency contact',
      hint: 'Critical for ambulance dispatch and safety scenarios.',
      kind: 'emergency_contact',
    },
    {
      done: Boolean(user?.self_enrolled_org?.id),
      label: 'Connect to your employer (optional)',
      hint: 'If your employer is an ICER customer, ask them for the invitation code.',
      kind: 'org',
    },
  ]
  const completed = checklist.filter((c) => c.done).length

  return (
    <div className="space-y-4">
      {showWelcome && (
        <Card>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-wantok-700">
                Welcome to Wantok Telehealth
              </div>
              <h2 className="text-xl font-display font-semibold text-slate-900 mt-1">
                Hi {fullName || 'there'} — let's set up your account
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                A complete profile means safer, more personal care.
                Finish these {checklist.length} items so your clinicians have what they need.
              </p>
            </div>
            <button
              onClick={dismissWelcome}
              className="text-xs text-slate-400 hover:text-slate-700"
              title="Dismiss"
            >Dismiss ✕</button>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] uppercase tracking-wide text-slate-500 font-bold">
                Onboarding checklist
              </span>
              <span className="text-xs text-slate-500">
                {completed} of {checklist.length} complete
              </span>
            </div>
            <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 transition-all"
                   style={{ width: `${(completed / checklist.length) * 100}%` }} />
            </div>

            <ul className="mt-3 space-y-2">
              {checklist.map((c, i) => {
                const isOpen = openItem === i
                const canEdit = !c.done && (
                  c.kind === 'date_of_birth' ||
                  c.kind === 'phone_number' ||
                  c.kind === 'emergency_contact' ||
                  c.kind === 'org'
                )
                return (
                  <li key={i} className="text-sm">
                    <div
                      role={canEdit ? 'button' : undefined}
                      tabIndex={canEdit ? 0 : undefined}
                      onClick={canEdit ? () => setOpenItem(isOpen ? null : i) : undefined}
                      className={
                        `flex items-start gap-2 ` +
                        (canEdit ? 'cursor-pointer hover:bg-wantok-50 -mx-2 px-2 py-1 rounded-md transition-colors' : '')
                      }
                    >
                      <span className={
                        `inline-flex items-center justify-center w-5 h-5 rounded-full text-[11px] font-bold border mt-0.5 shrink-0 ` +
                        (c.done ? 'bg-emerald-500 text-white border-emerald-500'
                                : 'bg-white text-slate-400 border-slate-300')
                      }>{c.done ? '✓' : i + 1}</span>
                      <span className="flex-1">
                        <span className={c.done ? 'text-slate-500 line-through' : 'text-slate-900 font-medium'}>
                          {c.label}
                        </span>
                        {!c.done && (
                          <span className="block text-[12px] text-slate-500 mt-0.5">{c.hint}</span>
                        )}
                      </span>
                      {canEdit && (
                        <span className="text-xs text-wantok-700 font-semibold">
                          {isOpen ? '✕' : '+ Add'}
                        </span>
                      )}
                    </div>
                    {isOpen && canEdit && (
                      <ChecklistInlineForm
                        kind={c.kind}
                        onCancel={() => setOpenItem(null)}
                        onSubmitProfile={(patch) => patchProfile.mutate(patch)}
                        onSubmitOrg={(code) => enrollOrg.mutate(code)}
                        busy={patchProfile.isPending || enrollOrg.isPending}
                        error={patchProfile.error || enrollOrg.error}
                      />
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title={`Welcome, ${fullName || 'patient'}`}
          subtitle="Your clinical timeline, lab/imaging orders, prescriptions, and uploaded documents."
          right={
            <div className="flex items-center gap-2">
              <a
                href="/patient/bookings"
                className="text-sm border border-wantok-700 text-wantok-700 hover:bg-wantok-50 rounded px-3 py-1.5 no-underline"
                title="See your upcoming and past consultations."
              >
                My bookings
              </a>
              <a
                href="/patient/book"
                className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-1.5 no-underline"
                title="Find a doctor and book a 15-minute consultation slot."
              >
                Book consultation
              </a>
            </div>
          }
        />
      </Card>

      {/* --- Discover services (marketplace) ----------------------------
          OTC pharmacies, lab tests, imaging studies, and ambulance
          services. Each tile takes the patient to a search/browse
          surface scoped to that service category. */}
      <Card>
        <CardHeader
          title="Discover services"
          subtitle="Browse what the network offers near you — pharmacies, labs, imaging centres, and ambulance services."
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <ServiceTile
            href="/patient/otc"
            icon="💊"
            title="OTC medicines"
            blurb="Common medicines you can buy without a prescription"
          />
          <ServiceTile
            href="/patient/labs"
            icon="🧪"
            title="Lab tests"
            blurb="Compare prices and turnaround across labs"
          />
          <ServiceTile
            href="/patient/imaging"
            icon="🩻"
            title="Imaging"
            blurb="X-ray, US, CT, MRI — find the right centre"
          />
          <ServiceTile
            href="/patient/ambulance"
            icon="🚑"
            title="Ambulance"
            blurb="Dispatch services across PNG"
          />
        </div>
      </Card>

      {/* --- Records (tabs replace the long-scroll list of cards) ---
          Notifications · Encounters · Prescriptions · Lab · Imaging ·
          Ambulance · Documents. Notifications is first so unread
          alerts are the first thing the patient sees. The badge on
          Notifications shows UNREAD count (coral when > 0); other
          tabs show total record count. Same prominent tab styling as
          the doctor / org / settings pages. */}
      {(() => {
        const RECORDS_TABS = [
          { id: 'notifications',  label: 'Notifications', count: unreadCount, urgent: unreadCount > 0 },
          { id: 'encounters',     label: 'Encounters',    count: (encQ.data || []).length },
          { id: 'prescriptions',  label: 'Prescriptions', count: (rxQ.data || []).length },
          { id: 'lab',            label: 'Lab orders',    count: (labQ.data || []).length },
          { id: 'imaging',        label: 'Imaging',       count: (imgQ.data || []).length },
          { id: 'ambulance',      label: 'Ambulance',     count: (ambQ.data || []).length },
          { id: 'documents',      label: 'Documents',     count: (docsQ.data || []).length },
        ]
        return (
          <>
            <div className="border-b-2 border-slate-200 flex flex-wrap gap-2">
              {RECORDS_TABS.map((t) => {
                const active = recordsTab === t.id
                return (
                  <button
                    key={t.id}
                    onClick={() => setRecordsTab(t.id)}
                    className={
                      `px-6 py-3 text-base tracking-wide border-b-[3px] -mb-[2px] transition-colors ` +
                      (active
                        ? 'border-wantok-700 text-wantok-800 font-bold bg-wantok-50/60'
                        : 'border-transparent text-slate-600 font-semibold hover:text-wantok-800 hover:bg-slate-50')
                    }
                  >
                    {t.label}
                    {t.count > 0 && (
                      <span className={
                        `ml-2 inline-flex items-center justify-center text-[11px] font-bold rounded-full ` +
                        `min-w-[20px] h-[20px] px-1.5 ` +
                        // Urgent (unread notifications) → coral red so
                        // the patient's eye catches it immediately,
                        // matching the bell badge in the top header.
                        (t.urgent
                          ? 'bg-coral-500 text-white'
                          : active
                            ? 'bg-wantok-100 text-wantok-800'
                            : 'bg-slate-100 text-slate-600')
                      }>{t.count}</span>
                    )}
                  </button>
                )
              })}
            </div>

            {/* --- Notifications --- */}
            {recordsTab === 'notifications' && (
              <Card>
                <CardHeader
                  title="Notifications"
                  subtitle={unreadCount ? `${unreadCount} unread` : 'All caught up.'}
                  right={
                    <button
                      onClick={() => markAll.mutate()}
                      disabled={!unreadCount}
                      className="text-xs text-wantok-700 underline font-semibold disabled:text-slate-400 disabled:no-underline disabled:font-normal"
                    >
                      Mark all read
                    </button>
                  }
                />
                {notifQ.error && <ErrorBanner error={notifQ.error} onRetry={() => notifQ.refetch()} />}
                <ul className="divide-y divide-slate-100">
                  {notifQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                  {!notifQ.isLoading && (notifQ.data || []).length === 0 && (
                    <li className="py-3 text-sm text-slate-400">No notifications.</li>
                  )}
                  {(notifQ.data || []).slice(0, 30).map((n) => (
                    <li key={n.id} className="py-2.5 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className={
                          `text-[15px] text-slate-900 leading-snug ` +
                          (n.read_at ? 'font-semibold' : 'font-bold')
                        }>
                          {n.title || n.kind}
                        </div>
                        <div className="text-xs text-slate-400 flex flex-wrap gap-2 items-center mt-1">
                          <span className="uppercase border rounded-full px-2 py-0.5 text-[10px] bg-slate-50 border-slate-200 text-slate-600 font-semibold">{n.kind}</span>
                          <span>{formatDateTime(n.created_at)}</span>
                          {n.body && <span className="truncate max-w-[50ch] text-slate-500">· {n.body}</span>}
                        </div>
                      </div>
                      {!n.read_at && (
                        <button
                          className="text-xs text-wantok-700 underline font-semibold shrink-0"
                          onClick={() => markRead.mutate(n.id)}
                        >Mark read</button>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* --- Encounters --- */}
            {recordsTab === 'encounters' && (
              <Card>
                <CardHeader title="My encounters" subtitle="Consultations on record" />
                {encQ.error && <ErrorBanner error={encQ.error} onRetry={() => encQ.refetch()} />}
                <ul className="divide-y divide-slate-100">
                  {encQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                  {!encQ.isLoading && (encQ.data || []).length === 0 && (
                    <li className="py-3 text-sm text-slate-400">No encounters yet.</li>
                  )}
                  {(encQ.data || []).slice(0, 10).map((e) => (
                    <li key={e.id} className="py-2">
                      <div className="text-sm font-medium">{e.chief_complaint || 'Encounter'}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                        <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(e.status)}`}>{e.status}</span>
                        <span>{formatDateTime(e.encounter_date || e.created_at)}</span>
                        {e.doctor_name && <span>· Dr {e.doctor_name}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* --- Prescriptions --- */}
            {recordsTab === 'prescriptions' && (
              <Card>
                <CardHeader title="My prescriptions" subtitle="Medications issued by your doctors — pickup location and dispensing status shown for each." />
                <ul className="divide-y divide-slate-100">
                  {rxQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                  {!rxQ.isLoading && (rxQ.data || []).length === 0 && (
                    <li className="py-3 text-sm text-slate-400">No prescriptions.</li>
                  )}
                  {(rxQ.data || []).slice(0, 10).map((p) => {
                    // Show a status chip that reflects pickup readiness,
                    // not internal admin lifecycle. If every item is
                    // dispensed → READY FOR PICKUP. Otherwise show the
                    // mixed/in-flight state.
                    const items = p.items || []
                    const statuses = items.map((i) => (i.dispensing_status || 'pending').toLowerCase())
                    const allDispensed = statuses.length > 0 && statuses.every((s) => s === 'dispensed')
                    const anyPending = statuses.some((s) => s === 'pending' || s === '')
                    const chipText = allDispensed
                      ? 'Ready for pickup'
                      : anyPending
                        ? 'In progress'
                        : 'Partial / substituted'
                    const chipCls = allDispensed
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : anyPending
                        ? 'bg-slate-100 text-slate-700 border-slate-200'
                        : 'bg-amber-50 text-amber-800 border-amber-200'
                    return (
                      <li key={p.id} className="py-2.5">
                        <div className="text-sm font-medium text-slate-900">
                          {items.length > 0
                            ? items.map((i) => `${i.drug_name}${i.dose ? ' ' + i.dose : ''}${i.quantity ? ' × ' + i.quantity : ''}`).join(' · ')
                            : `Prescription #${p.id}`}
                        </div>
                        <div className="text-xs flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                          <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] font-semibold ${chipCls}`}>
                            {chipText}
                          </span>
                          {p.pharmacy_name && (
                            <span className="text-slate-700">
                              <span className="text-[10px] uppercase tracking-wide text-slate-400 mr-1">Pickup at</span>
                              <span className="font-semibold">{p.pharmacy_name}</span>
                            </span>
                          )}
                          <span className="text-slate-400">· Issued {formatDateTime(p.created_at)}</span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </Card>
            )}

            {/* --- Lab orders --- */}
            {recordsTab === 'lab' && (
              <Card>
                <CardHeader title="Lab orders" subtitle="Pathology orders placed for you." />
                <ul className="divide-y divide-slate-100">
                  {labQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                  {!labQ.isLoading && (labQ.data || []).length === 0 && (
                    <li className="py-3 text-sm text-slate-400">None.</li>
                  )}
                  {(labQ.data || []).slice(0, 10).map((lo) => {
                    // Patient cares about the CLINICAL lifecycle
                    // (pending → preliminary → final), not the lab's
                    // internal queue admin status. Prefer result_status
                    // when set; fall back to admin status only when
                    // the result lifecycle hasn't started yet.
                    const displayStatus = (lo.result_status && lo.result_status !== 'pending')
                      ? lo.result_status
                      : (lo.status || 'pending')
                    return (
                      <li key={lo.id} className="py-2">
                        <div className="text-sm font-medium">
                          {(lo.items || []).map((i) => i.test_name).join(', ') || `Lab order #${lo.id}`}
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                          <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(displayStatus)}`}>{displayStatus}</span>
                          <span>{formatDateTime(lo.created_at)}</span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </Card>
            )}

            {/* --- Imaging --- */}
            {recordsTab === 'imaging' && (
              <Card>
                <CardHeader title="Imaging orders" subtitle="X-rays, ultrasounds, CT/MRI ordered for you." />
                <ul className="divide-y divide-slate-100">
                  {imgQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                  {!imgQ.isLoading && (imgQ.data || []).length === 0 && (
                    <li className="py-3 text-sm text-slate-400">None.</li>
                  )}
                  {(imgQ.data || []).slice(0, 10).map((io) => {
                    // Patient sees the CLINICAL lifecycle
                    // (pending → preliminary → final), not the
                    // radiology centre's queue admin status.
                    const displayStatus = (io.result_status && io.result_status !== 'pending')
                      ? io.result_status
                      : (io.status || 'pending')
                    return (
                      <li key={io.id} className="py-2">
                        <div className="text-sm font-medium">
                          {(io.items || []).map((i) => i.study_name).join(', ') || `Imaging order #${io.id}`}
                        </div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                          <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(displayStatus)}`}>{displayStatus}</span>
                          <span>{formatDateTime(io.created_at)}</span>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </Card>
            )}

            {/* --- Ambulance --- */}
            {recordsTab === 'ambulance' && (
              <Card>
                <CardHeader
                  title="Ambulance requests"
                  subtitle="Transfers ordered for you. Status updates as the crew progresses."
                />
                {ambQ.error && <ErrorBanner error={ambQ.error} onRetry={() => ambQ.refetch()} />}
                <ul className="divide-y divide-slate-100">
                  {ambQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                  {!ambQ.isLoading && (ambQ.data || []).length === 0 && (
                    <li className="py-3 text-sm text-slate-400">No ambulance requests.</li>
                  )}
                  {(ambQ.data || []).slice(0, 10).map((a) => (
                    <li key={a.id} className="py-2">
                      <div className="text-sm font-medium">
                        {a.service_type || 'Transport'}
                      </div>
                      <div className="text-xs text-slate-500 flex flex-wrap gap-2 mt-0.5">
                        <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(a.status)}`}>{a.status}</span>
                        {a.urgency && (
                          <span className="uppercase border rounded-full px-2 py-0.5 text-[10px] bg-slate-50 border-slate-200 text-slate-600">{a.urgency}</span>
                        )}
                        <span>{formatDateTime(a.created_at)}</span>
                      </div>
                      {(a.pickup_location || a.destination) && (
                        <div className="text-xs text-slate-500 mt-0.5">
                          {a.pickup_location || '—'} → {a.destination || '—'}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* --- Documents: list + self-upload --- */}
            {recordsTab === 'documents' && (
              <Card>
                <CardHeader title="My documents" subtitle="Scans, IDs, and records attached to your profile or encounters." />
                {docsQ.error && <ErrorBanner error={docsQ.error} onRetry={() => docsQ.refetch()} />}
                <ul className="divide-y divide-slate-100 mb-3">
                  {docsQ.isLoading && <li className="py-3 text-sm text-slate-400">Loading…</li>}
                  {!docsQ.isLoading && (docsQ.data || []).length === 0 && (
                    <li className="py-3 text-sm text-slate-400">No documents yet.</li>
                  )}
                  {(docsQ.data || []).slice(0, 20).map((d) => (
                    <li key={d.id} className="py-2 flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{d.original_filename || d.kind || `Document #${d.id}`}</div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-2">
                          <span className="uppercase border rounded-full px-2 py-0.5 text-[10px] bg-slate-50 border-slate-200 text-slate-600">{d.kind || 'document'}</span>
                          {d.size_bytes != null && <span>{Math.round(d.size_bytes / 1024)} KB</span>}
                          {d.mime_type && <span>{d.mime_type}</span>}
                          <span>{formatDateTime(d.created_at)}</span>
                        </div>
                        {d.description && <div className="text-xs text-slate-500 mt-0.5 truncate">{d.description}</div>}
                      </div>
                      {d.file_url && (
                        <a href={d.file_url} target="_blank" rel="noreferrer" className="text-xs text-wantok-700 underline shrink-0">Open</a>
                      )}
                    </li>
                  ))}
                </ul>

                <div className="border-t pt-3 space-y-2">
                  <div className="text-xs font-medium text-slate-700">Upload a new document to your profile</div>
                  <div className="grid gap-2 md:grid-cols-[1fr_12rem_8rem_5rem] items-center">
                    <input
                      type="file"
                      className="text-sm"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                    <input
                      type="text"
                      placeholder="Description (e.g. ID card front)"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      className="px-2 py-1 border rounded text-sm"
                    />
                    <select
                      value={kind}
                      onChange={(e) => setKind(e.target.value)}
                      className="px-2 py-1 border rounded text-sm"
                    >
                      <option value="id_document">ID document</option>
                      <option value="insurance_card">Insurance card</option>
                      <option value="prior_record">Prior medical record</option>
                      <option value="other">Other</option>
                    </select>
                    <button
                      onClick={() => upload.mutate()}
                      disabled={!file || upload.isPending}
                      className="px-3 py-1 bg-wantok-700 text-white text-sm rounded disabled:bg-slate-400"
                    >
                      {upload.isPending ? 'Uploading…' : 'Upload'}
                    </button>
                  </div>
                  {uploadErr && <ErrorBanner error={uploadErr} />}
                </div>
              </Card>
            )}
          </>
        )
      })()}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Inline form rendered under an unticked checklist row. Compact, no modal.
// `kind` controls which fields show; submit fires the right mutation.
// ---------------------------------------------------------------------------
function ChecklistInlineForm({ kind, onCancel, onSubmitProfile, onSubmitOrg, busy, error }) {
  const [dob, setDob] = useState('')
  const [phone, setPhone] = useState('')
  const [emName, setEmName] = useState('')
  const [emPhone, setEmPhone] = useState('')
  const [orgCode, setOrgCode] = useState('')

  function handleSubmit(e) {
    e.preventDefault()
    if (kind === 'date_of_birth') {
      if (!dob) return
      onSubmitProfile({ date_of_birth: dob })
    } else if (kind === 'phone_number') {
      if (!phone.trim()) return
      onSubmitProfile({ phone_number: phone.trim() })
    } else if (kind === 'emergency_contact') {
      if (!emName.trim() || !emPhone.trim()) return
      onSubmitProfile({
        emergency_contact_name: emName.trim(),
        emergency_contact_phone: emPhone.trim(),
      })
    } else if (kind === 'org') {
      if (!orgCode.trim()) return
      onSubmitOrg(orgCode.trim())
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="ml-7 mt-2 mb-1 p-3 bg-wantok-50/60 border border-wantok-100 rounded-lg space-y-2"
    >
      {kind === 'date_of_birth' && (
        <FieldInline label="Date of birth">
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                 autoFocus required
                 className="border rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400" />
        </FieldInline>
      )}

      {kind === 'phone_number' && (
        <FieldInline label="Phone number" hint="PNG format: +675 followed by your number">
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                 autoFocus required placeholder="+675 7000 1234"
                 className="w-full border rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400" />
        </FieldInline>
      )}

      {kind === 'emergency_contact' && (
        <div className="grid sm:grid-cols-2 gap-2">
          <FieldInline label="Contact name">
            <input value={emName} onChange={(e) => setEmName(e.target.value)}
                   autoFocus required placeholder="Spouse, parent, sibling…"
                   className="w-full border rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400" />
          </FieldInline>
          <FieldInline label="Contact phone">
            <input type="tel" value={emPhone} onChange={(e) => setEmPhone(e.target.value)}
                   required placeholder="+675 …"
                   className="w-full border rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400" />
          </FieldInline>
        </div>
      )}

      {kind === 'org' && (
        <FieldInline label="Organisation invitation code"
                     hint="Ask your employer's HR / wellness lead for this code.">
          <input value={orgCode} onChange={(e) => setOrgCode(e.target.value.toUpperCase())}
                 autoFocus required placeholder="e.g. OPIC2026"
                 className="w-full border rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-wantok-400 font-mono" />
        </FieldInline>
      )}

      {error && (
        <div className="text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 text-xs">
          {String(error?.response?.data?.error?.message
                  || error?.response?.data?.detail
                  || error?.message
                  || 'Could not save. Please try again.')}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button type="submit" disabled={busy}
                className="text-xs font-semibold bg-wantok-700 hover:bg-wantok-800 text-white rounded-full px-4 py-1.5 disabled:opacity-60">
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button type="button" onClick={onCancel}
                className="text-xs text-slate-500 hover:text-slate-800">
          Cancel
        </button>
      </div>
    </form>
  )
}

function FieldInline({ label, hint, children }) {
  return (
    <label className="block text-xs text-slate-700 font-semibold">
      {label}
      <div className="mt-1">{children}</div>
      {hint && <div className="text-[11px] text-slate-500 font-normal mt-1">{hint}</div>}
    </label>
  )
}

// ---------------------------------------------------------------------------
// One tile in the "Discover services" grid. Stays clickable as a single
// surface — picking the right marketplace happens in one tap rather than
// drilling through nested menus, which matters on the low-end Android
// devices that dominate the PNG patient population.
// ---------------------------------------------------------------------------
function ServiceTile({ href, icon, title, blurb }) {
  return (
    <a
      href={href}
      className="block border border-slate-200 hover:border-wantok-300 hover:bg-wantok-50/40 rounded-lg p-3 no-underline transition-colors"
    >
      <div className="text-2xl mb-1" aria-hidden="true">{icon}</div>
      <div className="text-sm font-semibold text-slate-800">{title}</div>
      <div className="text-[11px] text-slate-500 mt-0.5 leading-snug">{blurb}</div>
    </a>
  )
}
